import { useState, type KeyboardEvent, useRef, useEffect } from 'react'
import { trackEvent } from '@/utils/analytics'
import { Send, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useAppStore } from '@/store'
import { toast } from 'sonner'
import MentionAutocomplete from './MentionAutocomplete'
import { useTranslation } from '@/i18n'

interface CommentInputProps {
  workspaceId: string
  parentCommentId?: string | null
  placeholder?: string
  onCommentCreated?: () => void
  onCancel?: () => void
  autoFocus?: boolean
}

interface MentionState {
  isActive: boolean
  query: string
  startPos: number
  position: { top: number; left: number }
}

interface MentionedUser {
  id: string
  display: string
}

const CommentInput = ({
  workspaceId,
  parentCommentId = null,
  placeholder,
  onCommentCreated,
  onCancel,
  autoFocus = false,
}: CommentInputProps) => {
  const { t } = useTranslation()
  const defaultPlaceholder = parentCommentId ? t('common:comments.reply_placeholder') : t('common:comments.placeholder')
  const [inputValue, setInputValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [mentionState, setMentionState] = useState<MentionState>({
    isActive: false,
    query: '',
    startPos: 0,
    position: { top: 0, left: 0 },
  })
  const [mentionedUsers, setMentionedUsers] = useState<MentionedUser[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { createComment } = useAppStore()

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [autoFocus])

  const handleInputChange = (value: string) => {
    setInputValue(value)

    // Use setTimeout to ensure cursor position is updated after value change
    setTimeout(() => {
      // Check for @ mention trigger
      const cursorPos = textareaRef.current?.selectionStart || 0
      const textBeforeCursor = value.slice(0, cursorPos)
      const lastAtIndex = textBeforeCursor.lastIndexOf('@')

      if (lastAtIndex !== -1) {
        const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1)

        // Check if there's no space after @ (active mention)
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          // Calculate position for autocomplete dropdown
          if (textareaRef.current) {
            const textarea = textareaRef.current
            const rect = textarea.getBoundingClientRect()

            setMentionState({
              isActive: true,
              query: textAfterAt,
              startPos: lastAtIndex,
              position: {
                top: rect.bottom + 5,
                left: rect.left,
              },
            })
          }
          return
        }
      }

      // Close mention autocomplete if no active mention
      if (mentionState.isActive) {
        setMentionState({ ...mentionState, isActive: false })
      }
    }, 0)
  }

  const handleMentionSelect = (user: {
    user_id: string
    name: string | null
    email: string | null
  }) => {
    const displayName = user.name || user.email || 'Unknown User'
    const beforeMention = inputValue.slice(0, mentionState.startPos)
    const afterMention = inputValue.slice(
      textareaRef.current?.selectionStart || 0
    )
    const newValue = `${beforeMention}@${displayName} ${afterMention}`

    setInputValue(newValue)
    setMentionedUsers([
      ...mentionedUsers,
      { id: user.user_id, display: displayName },
    ])
    setMentionState({ ...mentionState, isActive: false })

    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos =
          mentionState.startPos + displayName.length + 2 // +2 for @ and space
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
      }
    }, 0)
  }

  const handleSubmit = async () => {
    const trimmedValue = inputValue.trim()
    if (!trimmedValue || isSubmitting) return

    setIsSubmitting(true)
    try {
      // Extract mentioned user IDs from tracked mentions
      const mentionedUserIds = extractMentionedUserIds(
        trimmedValue,
        mentionedUsers
      )

      await createComment(workspaceId, {
        content: trimmedValue,
        parent_comment_id: parentCommentId,
        mentioned_user_ids: mentionedUserIds,
      })

      setInputValue('')
      setMentionedUsers([])
      trackEvent('comment-created', {
        workspace_id: workspaceId,
        is_reply: !!parentCommentId,
        mention_count: mentionedUserIds.length,
        content_length: trimmedValue.length,
      })
      onCommentCreated?.()
      toast.success(parentCommentId ? t('common:comments.reply_added') : t('common:comments.comment_added'))
    } catch (error) {
      toast.error(t('common:comments.failed_add'))
      console.error('Create comment error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const extractMentionedUserIds = (
    text: string,
    users: MentionedUser[]
  ): string[] => {
    // Extract user IDs from mentions that appear in the text
    const userIds: string[] = []
    users.forEach((user) => {
      if (text.includes(`@${user.display}`)) {
        userIds.push(user.id)
      }
    })
    return [...new Set(userIds)] // Remove duplicates
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Don't handle Enter if mention autocomplete is active
    if (mentionState.isActive && ['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(e.key)) {
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    } else if (e.key === 'Escape' && onCancel) {
      onCancel()
    }
  }

  const handleCancel = () => {
    setInputValue('')
    setMentionedUsers([])
    setMentionState({ ...mentionState, isActive: false })
    onCancel?.()
  }

  return (
    <div className="flex flex-col gap-2 relative">
      {parentCommentId && (
        <div
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
        >
          <span>{t('common:comments.replying')}</span>
          {onCancel && (
            <button
              onClick={handleCancel}
              className="hover:opacity-70 transition-opacity"
            >
              <X size={16} />
            </button>
          )}
        </div>
      )}
      <div className="flex gap-2 items-end">
        <Textarea
          ref={textareaRef}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || defaultPlaceholder}
          disabled={isSubmitting}
          className="flex-1 min-h-[80px] max-h-[200px] resize-none border-gray-300 dark:border-gray-600"
        />
        <Button
          onClick={handleSubmit}
          disabled={!inputValue.trim() || isSubmitting}
          size="icon"
          className="h-10 w-10"
        >
          <Send size={18} />
        </Button>
      </div>
      <div className="text-xs text-gray-500 dark:text-dark-text-muted">
        {t('common:comments.hint')}
      </div>

      {mentionState.isActive && (
        <MentionAutocomplete
          query={mentionState.query}
          onSelect={handleMentionSelect}
          onClose={() => setMentionState({ ...mentionState, isActive: false })}
          position={mentionState.position}
        />
      )}
    </div>
  )
}

export default CommentInput
